import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, FlaskConical, PenTool, Cpu, Layers, Gauge, Rocket } from "lucide-react";
import { useInitiatives } from "@/lib/useInitiatives";
import InitiativeCard from "@/components/InitiativeCard";

const CHAIN = [
  { key: "research", icon: FlaskConical },
  { key: "design", icon: PenTool },
  { key: "prototype", icon: Cpu },
  { key: "package", icon: Layers },
  { key: "test", icon: Gauge },
  { key: "deploy", icon: Rocket },
];

const PARTNERS = [
  { id: "duk", name: "Digital University Kerala", logo: "/partners/duk.png", href: "https://duk.ac.in" },
  { id: "kspace", name: "Kerala Space Park (K-SPACE)", logo: "/partners/kspace.png", href: "https://kspace.kerala.gov.in" },
  { id: "ksum", name: "Kerala Startup Mission (KSUM)", logo: "/partners/ksum.svg", href: "https://startupmission.kerala.gov.in" },
];

export default function Home() {
  const { t } = useTranslation();
  const { byCategory } = useInitiatives();
  const mission = byCategory.mission?.[0];

  const STATS = [
    { value: "100,000", label: t("home.stats.learners") },
    { value: "5,000+", label: t("home.stats.professionals") },
    { value: "100", label: t("home.stats.ventures") },
    { value: "20–30", label: t("home.stats.tapeouts") },
  ];

  return (
    <div>
      {/* ---------- Hero ---------- */}
      <section className="relative band-dark circuit-bg overflow-hidden" aria-labelledby="hero-heading">
        <img
          src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=2000&q=80"
          alt=""
          role="presentation"
          className="absolute inset-y-0 right-0 w-[55%] h-full object-cover opacity-25 mix-blend-luminosity"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-28 lg:pb-24 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="tag mb-6 rise-in" data-testid="hero-tag">{t("home.eyebrow")}</div>
            <h1 id="hero-heading" className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] rise-in rise-in-1">
              {t("home.title_a")} <span className="text-amber">{t("home.title_b")}</span> {t("home.title_c")}
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/85 max-w-2xl leading-relaxed rise-in rise-in-2">{t("home.intro")}</p>
            <div className="mt-10 flex flex-wrap gap-3 rise-in rise-in-3">
              <Link
                to={mission ? `/initiatives/${mission.slug}` : "/initiatives"}
                data-testid="hero-cta-mission"
                className="group inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white px-6 py-3.5 font-medium transition-colors"
              >
                {t("home.cta_mission")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
              <Link
                to="/chipmat"
                data-testid="hero-cta-chipmat"
                className="inline-flex items-center gap-2 bg-amber text-brand-deep px-6 py-3.5 font-semibold hover:brightness-105 transition"
              >
                {t("home.cta_chipmat")}
              </Link>
              <Link
                to="/startups"
                data-testid="hero-cta-startups"
                className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white px-6 py-3.5 font-medium transition-colors"
              >
                {t("home.cta_startups")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 rise-in rise-in-4">
            <div className="bg-white p-5 mb-px">
              <img src="/semicon-keralam-logo.png" alt={t("meta.brand")} className="h-24 w-auto mx-auto" />
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/15" aria-label="Key statistics">
              {STATS.map((s) => (
                <div key={s.label} className="bg-brand-deep/80 p-5">
                  <div className="font-display font-black text-white text-2xl">{s.value}</div>
                  <div className="mt-1 font-mono text-[10px] tracking-widest text-white/75 uppercase">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Value chain ---------- */}
      <section className="py-20 border-b border-ink/10 circuit-bg-light" aria-labelledby="chain-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-5">
              <div className="tag mb-3">{t("home.value_chain_eyebrow")}</div>
              <h2 id="chain-heading" className="font-display font-black text-brand text-3xl sm:text-4xl leading-tight">{t("home.value_chain_title")}</h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-ink/75 text-base leading-relaxed self-end">{t("home.value_chain_desc")}</p>
          </div>
          <ol className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-ink/10 list-none p-0" data-testid="value-chain">
            {CHAIN.map((c, i) => {
              const Icon = c.icon;
              return (
                <li key={c.key} className="bg-white p-6 relative">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-10 h-10 border border-teal/40 flex items-center justify-center">
                      <Icon className="w-4.5 h-4.5 text-teal" aria-hidden="true" />
                    </div>
                    <span className="font-mono text-[10px] text-ink/50">0{i + 1}</span>
                  </div>
                  <div className="font-display font-bold text-brand text-lg">{t(`home.chain.${c.key}`)}</div>
                  <div className="mt-1 text-ink/65 text-xs leading-relaxed">{t(`home.chain.${c.key}_d`)}</div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ---------- Centres ---------- */}
      <Group
        id="centres"
        eyebrow={t("home.centres_eyebrow")}
        title={t("home.centres_title")}
        desc={t("home.centres_desc")}
        items={byCategory.centre}
        viewAll={t("home.view_all")}
        className="bg-surface"
      />

      {/* ---------- CHIPMAT callout ---------- */}
      <section className="py-16 border-y border-ink/10" aria-labelledby="chipmat-heading" data-testid="chipmat-callout">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="tag mb-3">{t("home.chipmat_eyebrow")}</div>
            <h2 id="chipmat-heading" className="font-display font-black text-brand text-3xl sm:text-4xl leading-tight">{t("home.chipmat_title")}</h2>
            <p className="mt-5 text-ink/75 leading-relaxed max-w-2xl">{t("home.chipmat_body")}</p>
            <Link to="/chipmat" data-testid="home-chipmat-cta" className="mt-8 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white px-6 py-3.5 font-medium transition-colors">
              {t("home.chipmat_cta")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="lg:col-span-5 grid grid-cols-3 gap-px bg-ink/10">
            {[["₹40 L", "per project"], ["TRL 4+", "maturity"], ["2%", "equity · 20 cos."]].map(([v, l]) => (
              <div key={l} className="bg-white p-6 border-t-2 border-amber">
                <div className="font-display font-black text-brand text-3xl">{v}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-ink/60">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Programmes + Incubation ---------- */}
      <Group id="programmes" eyebrow={t("home.programmes_eyebrow")} title={t("home.programmes_title")} items={byCategory.programme} compact />
      <Group id="incubation" eyebrow={t("home.incubation_eyebrow")} title={t("home.incubation_title")} items={byCategory.incubation} compact className="bg-surface" />

      {/* ---------- Partner logos ---------- */}
      <section className="py-16 bg-surface border-y border-ink/10" aria-labelledby="partners-heading" data-testid="partners-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="tag mb-3">{t("home.partners_eyebrow")}</div>
          <h2 id="partners-heading" className="font-display font-black text-brand text-2xl sm:text-3xl leading-tight max-w-2xl">{t("home.partners_title")}</h2>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-ink/45">{t("home.partners_label")}</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-ink/10">
            {PARTNERS.map((p) => (
              <a
                key={p.name}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                title={p.name}
                data-testid={`partner-${p.id}`}
                className="group bg-white flex flex-col items-center justify-center gap-4 p-10 transition-colors hover:bg-base"
                aria-label={p.name}
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  className="max-h-16 w-auto object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition duration-300"
                />
                <span className="text-[11px] text-ink/55 group-hover:text-ink text-center leading-tight transition-colors">{p.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Roadmap ---------- */}
      <section className="py-20 band-dark circuit-bg" aria-labelledby="roadmap-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="tag mb-3">{t("home.roadmap_eyebrow")}</div>
          <h2 id="roadmap-heading" className="font-display font-black text-white text-3xl sm:text-4xl mb-12">{t("home.roadmap_title")}</h2>
          <ol className="grid md:grid-cols-3 gap-px bg-white/15 list-none p-0">
            {["phase1", "phase2", "phase3"].map((p, i) => (
              <li key={p} className="bg-brand-deep/70 p-8 border-t-2" style={{ borderColor: ["#00886A", "#F5A21C", "#0068B8"][i] }}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 mb-3">0{i + 1}</div>
                <h3 className="font-display font-bold text-white text-lg">{t(`home.${p}`)}</h3>
                <p className="mt-3 text-white/80 text-sm leading-relaxed">{t(`home.${p}_d`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}

function Group({ id, eyebrow, title, desc, items, viewAll, compact = false, className = "" }) {
  return (
    <section id={id} className={`py-20 ${className}`} aria-labelledby={`${id}-heading`} data-testid={`${id}-section`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div className="max-w-2xl">
            <div className="tag mb-3">{eyebrow}</div>
            <h2 id={`${id}-heading`} className="font-display font-black text-brand text-3xl sm:text-4xl">{title}</h2>
            {desc && <p className="mt-4 text-ink/75 text-base leading-relaxed">{desc}</p>}
          </div>
          {viewAll && (
            <Link to="/initiatives" data-testid={`${id}-view-all`} className="inline-flex items-center gap-2 text-teal font-medium text-sm hover:text-teal-dark">
              {viewAll} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          )}
        </div>
        <div className={`grid gap-4 ${compact ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {items.map((it, i) => <InitiativeCard key={it.slug} item={it} index={i} compact={compact} />)}
        </div>
      </div>
    </section>
  );
}
